import React, { useEffect, useRef, useState } from "react";
import NoAvailablePaymentMethod from "../../../../Assets/CustomerDetailPage/NoAvailablePaymentMethod.svg";
import { ReactComponent as KebabMenuIcon } from "../../../../Assets/ActionIcons/KebabMenuIcon.svg";
import { ReactComponent as MinusIcon } from "../../../../Assets/ActionIcons/MinusIcon.svg";
import { useCustomerStore } from "../../../../Store/Customer";
import { AdyenPaymentMethodType, AllowedPaymentMethod } from "../../../../Contracts/Transaction";
import { useAuth0 } from "@auth0/auth0-react";
import { PaymentMethodDisplayAttributes } from "../../../../Contracts/Finance";

const CustomerAllowedPaymentMethodsCard: React.FC = () => {
  const { isLoading, customerDetailEntity, updateAllowedPaymentMethods } = useCustomerStore();
  const [addPaymentMethodTypeOpen, setAddPaymentMethodTypeOpen] = useState(false);
  const [methodTypeActionMenuOpen, setMethodTypeActionMenuOpen] = useState("");
  const [dirtyPaymentMethods, setDirtyPaymentMethods] = useState<AllowedPaymentMethod[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const removeMethodRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setAddPaymentMethodTypeOpen(false);
      }
      if (removeMethodRef.current && !removeMethodRef.current.contains(event.target as Node)) {
        setMethodTypeActionMenuOpen("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const allowedPaymentMethods =
    customerDetailEntity?.allowed_payment_methods?.data
      .filter(m => m.enabled)
      .map(m => new PaymentMethodDisplayAttributes(m)) || [];

  const notEnabledPaymentMethods = customerDetailEntity?.allowed_payment_methods?.data?.filter(
    m => !m.enabled
  );

  const notEnabledPaymentMethodsWithDirtyChanges = notEnabledPaymentMethods?.map(m => {
    const dirtyMethod = dirtyPaymentMethods.find(dm => dm.type === m.type);
    if (dirtyMethod) {
      return dirtyMethod;
    }
    return m;
  });

  const { getAccessTokenSilently } = useAuth0();

  const onAddPaymentMethods = async () => {
    setDirtyPaymentMethods([]);
    setAddPaymentMethodTypeOpen(false);
    await updateAllowedPaymentMethods(getAccessTokenSilently, dirtyPaymentMethods);
  };

  const methodRemoveClicked = async (methodType: AdyenPaymentMethodType) => {
    setMethodTypeActionMenuOpen("");
    if (window.confirm("Are you sure you want to remove this payment method for this client?")) {
      const targetMethod = customerDetailEntity?.allowed_payment_methods?.data.find(
        m => m.type === methodType
      );
      if (targetMethod) {
        await updateAllowedPaymentMethods(getAccessTokenSilently, [
          {
            ...targetMethod,
            enabled: false
          }
        ]);
      }
    }
  };

  return (
    <div className="rounded p-3 border border-gray-300 my-2">
      <div className="flex items-center justify-between mb-4">
        <span className="font-semibold">Allowed payment methods</span>
        <div className="relative flex-grow flex justify-end">
          <button
            className="flex space-x-2 items-center text-blue-600"
            onClick={() => setAddPaymentMethodTypeOpen(!addPaymentMethodTypeOpen)}
          >
            <span>Add another method</span>
            <div className="text-xs">{addPaymentMethodTypeOpen ? "▲" : "▼"}</div>
          </button>
          {addPaymentMethodTypeOpen && (
            <div
              ref={dropdownRef}
              className="absolute top-full right-0 flex flex-col bg-white border border-gray-200 rounded w-auto z-40"
            >
              {notEnabledPaymentMethodsWithDirtyChanges &&
              notEnabledPaymentMethodsWithDirtyChanges.length > 0 ? (
                <>
                  <div className="p-3 flex flex-col space-y-2">
                    {notEnabledPaymentMethodsWithDirtyChanges?.map(m => {
                      const method = new PaymentMethodDisplayAttributes(m);
                      return (
                        <div key={method.type} className="flex items-center overflow-hidden">
                          <input
                            type="checkbox"
                            className="mr-2"
                            onChange={event => {
                              if (event.target.checked) {
                                setDirtyPaymentMethods([
                                  ...dirtyPaymentMethods,
                                  {
                                    ...m,
                                    enabled: true
                                  }
                                ]);
                              } else {
                                setDirtyPaymentMethods(
                                  dirtyPaymentMethods.filter(dm => dm.type !== method.type)
                                );
                              }
                            }}
                          />
                          <img src={method.logoSrc} alt={method.type} />
                          <div className="whitespace-nowrap">{method.displayName}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-end border-t border-gray-200 p-2">
                    <button
                      className={`text-white px-3 py-2 rounded-lg ${
                        dirtyPaymentMethods.length === 0
                          ? "cursor-not-allowed bg-gray-300"
                          : "bg-blue-600 hover:bg-blue-500"
                      }${isLoading ? " cursor-progress" : ""}`}
                      onClick={onAddPaymentMethods}
                      disabled={isLoading || dirtyPaymentMethods.length === 0}
                    >
                      Add
                    </button>
                  </div>
                </>
              ) : (
                <div className="p-3 flex flex-col space-y-2">
                  <span>All payment methods are already added</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {allowedPaymentMethods.map(method => (
        <div
          className="relative flex justify-between p-2 border-t border-gray-300"
          key={method.type}
        >
          <div
            className={`${isLoading ? "hidden" : "visible"}  flex items-center`}
            key={method.type}
          >
            <img src={method.logoSrc} alt={method.displayName} className="mr-4" />
            <span>{method.displayName}</span>
          </div>
          <button
            onClick={() => setMethodTypeActionMenuOpen(method.type)}
            className="text-blue-600"
          >
            <KebabMenuIcon />
          </button>
          {methodTypeActionMenuOpen === method.type && (
            <div
              className="absolute top-2/3 right-3 flex flex-col bg-white border border-gray-200 rounded w-auto z-20 shadow-lg"
              ref={removeMethodRef}
            >
              <button
                className="p-3 flex hover:bg-gray-100 items-center space-x-2"
                onClick={() => methodRemoveClicked(method.type)}
              >
                <MinusIcon />
                <div>Remove method</div>
              </button>
            </div>
          )}
        </div>
      ))}
      {isLoading &&
        [1, 2, 3].map(index => (
          <div key={index} className="p-2 border-t border-gray-300 flex items-center animate-pulse">
            <div className="bg-slate-200 h-6 w-6 rounded-full mr-4"></div>
            <div className="bg-slate-200 h-6 w-20 rounded-lg"></div>
          </div>
        ))}
      {!isLoading && allowedPaymentMethods.length === 0 && (
        <div className="flex flex-col items-center justify-center border-t border-gray-300 p-4">
          <img src={NoAvailablePaymentMethod} alt="no avilable payment methods" />
          <span>This client has no allowed payment methods yet</span>
        </div>
      )}
    </div>
  );
};

export default CustomerAllowedPaymentMethodsCard;
